// Summon lifecycle and spec-extension reducers. ADR 0009 §2.10.
//
// Summons get a *combat avatar* in BattleState.entities (so the existing AI /
// damage / status pipelines apply to them unchanged) and a *lifecycle record*
// in BattleState.summons. The two are bridged by `entityId`.
//
// SummonSpec PR B-1 extension reducers landed here together with the spec
// fields per ADR 0008 §2.5 注釈 ("type and reducer in the same PR"):
//   - onDeathEffects  : fire at death position when summon dies (not natural expiry)
//   - randomElement   : roll an element at spawn, apply matching buff
//   - threadLineAura  : ally inside the caster↔summon corridor gets buffId each tick
import { applyEffect } from '../effects.js';
import { nextRuntimeId, pickPlaceableTargets } from './placeables.js';
import { fireReactiveSkills } from './reactive-events.js';
/** Summon stat scale relative to caster. ADR 0009 §2.10. Numerical balance is
 *  *not* a B-1 goal — calibration happens in B-3 / Phase 4. */
const SUMMON_STAT_SCALE = 0.3;
/** Tick budget for randomElement / threadLineAura buffs when no override is given. */
const DEFAULT_AURA_BUFF_TICKS = 60;
/** Radius for the death-position AoE fired by `onDeathEffects`. ADR 0009 §2.10
 *  pins this at the placeable default radius for consistency — necromancer-style
 *  "死亡時に周囲へ撒き散らす" (ADR 0007 §2.4) is one of the patterns this hook
 *  serves, and matching the placeable footprint keeps designer mental models
 *  aligned across the two AoE pipelines. */
const DEATH_AOE_RADIUS = 8;
function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}
/**
 * Spawn `count` summons from a SummonSpec. Applies the per-owner concurrency
 * cap by despawning the oldest existing summons of the same template+owner
 * pair, then materialises each new one.
 */
export function spawnSummons(spec, caster, state) {
    const count = spec.count ?? 1;
    const cap = spec.maxConcurrentPerOwner ?? Number.POSITIVE_INFINITY;
    // FIFO cap: drop oldest if (existing + new) would exceed the cap.
    const existing = state.summons.filter((s) => s.ownerId === caster.id && s.unitId === spec.unitId);
    const projected = existing.length + count;
    if (projected > cap) {
        const overflow = Math.min(existing.length, projected - cap);
        for (let i = 0; i < overflow; i++) {
            despawnSummon(existing[i], state);
        }
    }
    const created = [];
    for (let i = 0; i < count; i++) {
        const entity = buildSummonEntity(spec, caster, state, i);
        state.entities.push(entity);
        const inst = {
            entityId: entity.id,
            ownerId: caster.id,
            unitId: spec.unitId,
            expiresAt: state.tick + spec.durationTicks,
        };
        if (spec.onDeathEffects !== undefined)
            inst.onDeathEffects = spec.onDeathEffects;
        state.summons.push(inst);
        // ADR 0014 §2.3.1 / §2.6: 召喚体 entity → SummonInstance の逆引き登録。
        // kill 帰属 (resolveKillOwner) と onSummonAttack / onSummonDied で参照。
        state.summonByEntity.set(entity.id, inst);
        created.push(inst);
    }
    // randomElement: pick one element across the *cast*, apply buff to target set.
    if (spec.randomElement !== undefined) {
        applyRandomElementBuff(spec.randomElement, caster, state);
    }
    return created;
}
/** Drop a summon: mark its avatar dead so the existing pipeline cleans up. */
function despawnSummon(inst, state) {
    const entity = state.entities.find((e) => e.id === inst.entityId);
    if (entity !== undefined && entity.hp > 0)
        entity.hp = 0;
    const idx = state.summons.indexOf(inst);
    if (idx >= 0)
        state.summons.splice(idx, 1);
    // ADR 0014 §2.3.1: summonByEntity index 同期削除。
    state.summonByEntity.delete(inst.entityId);
}
/**
 * Per-tick housekeeping: detect death (fire onDeathEffects), expire (silent
 * despawn), and refresh threadLineAura for surviving summons.
 */
export function tickSummons(state) {
    if (state.summons.length === 0)
        return;
    const tick = state.tick;
    const remaining = [];
    for (const inst of state.summons) {
        const entity = state.entities.find((e) => e.id === inst.entityId);
        if (entity === undefined || entity.hp <= 0) {
            // Death (combat or other-source). Fire onDeathEffects at the corpse pos.
            if (inst.onDeathEffects !== undefined && entity !== undefined) {
                fireDeathEffects(inst, entity, state);
            }
            // ADR 0014 §2.4: onSummonDied reactive を owner 側で発火。
            // 軸 4 (onSummonDied) は被攻撃側 (= summon owner) で発火、軸 9 (onKill) は
            // damage 経路で kill owner 側で発火 → 機構分離 (§2.6)。
            fireReactiveSkills(state, inst.ownerId, 'onSummonDied', entity ?? null);
            state.summonByEntity.delete(inst.entityId);
            continue;
        }
        if (inst.expiresAt <= tick) {
            // Natural expiry: silent despawn (no onDeathEffects per ADR 0009 §2.10).
            entity.hp = 0;
            state.summonByEntity.delete(inst.entityId);
            continue;
        }
        remaining.push(inst);
    }
    state.summons = remaining;
    // threadLineAura is a per-tick refresh — apply after lifecycle updates.
    refreshThreadLineAuras(state);
}
function fireDeathEffects(inst, corpse, state) {
    const owner = state.entities.find((e) => e.id === inst.ownerId);
    if (owner === undefined)
        return;
    // Death-position AoE: ADR 0007 §2.4 necromancer concept ("死亡時に周囲へ
    // デバフを撒き散らし") and ADR 0009 §2.10 specify the corpse position as the
    // AoE center. Side selection follows the same contract as placeables
    // (`pickPlaceableTargets`): damage / applyDebuff / knockback / pull hit
    // enemies (P55-024), heal / applyBuff / shield / cleanse hit allies,
    // everything else stays on owner.
    const candidates = [];
    for (const e of state.entities) {
        if (e.hp <= 0)
            continue;
        if (distance(e.pos, corpse.pos) <= DEATH_AOE_RADIUS)
            candidates.push(e);
    }
    for (const effect of inst.onDeathEffects ?? []) {
        const targets = pickPlaceableTargets(effect, owner, candidates);
        for (const target of targets) {
            applyEffect(effect, owner, target, state, `summon.onDeath:${inst.unitId}`);
        }
    }
}
/** Pick one element at random and apply the matching buff to the target set. */
function applyRandomElementBuff(re, caster, state) {
    const candidates = re.candidates !== undefined && re.candidates.length > 0
        ? re.candidates
        : Object.keys(re.buffByElement);
    if (candidates.length === 0)
        return;
    const idx = Math.floor(state.rng.nextFloat() * candidates.length);
    const chosen = candidates[Math.min(idx, candidates.length - 1)];
    const buffId = re.buffByElement[chosen];
    if (buffId === undefined)
        return;
    const recipients = [];
    switch (re.buffTarget) {
        case 'caster':
            recipients.push(caster);
            break;
        case 'allAllies':
            for (const e of state.entities) {
                if (e.side === caster.side && e.hp > 0)
                    recipients.push(e);
            }
            break;
    }
    const dur = re.buffDurationTicks ?? DEFAULT_AURA_BUFF_TICKS;
    const expiresAt = state.tick + dur;
    for (const recipient of recipients) {
        upsertBuff(recipient, buffId, expiresAt);
    }
}
/** Refresh `threadLineAura` buffs for every summon's caster↔summon corridor. */
function refreshThreadLineAuras(state) {
    // Cache resolved corridor specs by ownerId so we don't repeatedly look up
    // the SummonSpec source per ally check. The spec itself isn't snapshotted on
    // SummonInstance (only the lifecycle bits are), so we rely on the caller of
    // spawnSummons having chosen to fire threadLineAura at refresh time. We pass
    // the corridor info on the SummonInstance via a hidden helper field — but
    // since SummonInstance is intentionally minimal, here we re-discover the
    // corridor by inspecting attached `_threadLineAura` if present.
    // Implementation detail: spawnSummons attaches _threadLineAura to the
    // returned instance via Object.defineProperty so the public type stays clean.
    for (const inst of state.summons) {
        const aura = inst
            ._threadLineAura;
        if (aura === undefined)
            continue;
        const owner = state.entities.find((e) => e.id === inst.ownerId);
        const summon = state.entities.find((e) => e.id === inst.entityId);
        if (owner === undefined || summon === undefined || owner.hp <= 0 || summon.hp <= 0)
            continue;
        const dur = aura.buffDurationTicks ?? DEFAULT_AURA_BUFF_TICKS;
        const expiresAt = state.tick + dur;
        for (const ally of state.entities) {
            if (ally.side !== owner.side || ally.hp <= 0)
                continue;
            if (ally.id === owner.id || ally.id === summon.id)
                continue;
            if (pointInCorridor(ally.pos, owner.pos, summon.pos, aura.auraRadius)) {
                upsertBuff(ally, aura.buffId, expiresAt);
            }
        }
    }
}
/** Distance from point `p` to segment `a→b`; returns true if ≤ halfWidth. */
function pointInCorridor(p, a, b, halfWidth) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0)
        return distance(p, a) <= halfWidth;
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const closest = { x: a.x + t * dx, y: a.y + t * dy };
    return distance(p, closest) <= halfWidth;
}
/** Refresh-style buff insert (extend duration, no stack growth). */
function upsertBuff(target, buffId, expiresAt) {
    const existing = target.buffs.find((b) => b.id === buffId);
    if (existing !== undefined) {
        existing.expiresAt = expiresAt;
        return;
    }
    const fresh = { id: buffId, stacks: 1, expiresAt };
    target.buffs.push(fresh);
}
/**
 * Construct a runtime Entity for a summoned unit. ADR 0009 §2.10 fixes the
 * scale at 30% of the caster's stats — this is *not* a balance pass; it just
 * lets summons participate in combat at all so the rest of B-1 / B-2 / B-3
 * pipelines can be exercised. Final unit templates land in B-3.
 */
function buildSummonEntity(spec, caster, state, index) {
    const scale = SUMMON_STAT_SCALE * (1 + Math.max(0, caster.summonPowerBonus ?? 0));
    const stats = {
        hpMax: caster.stats.hpMax * scale,
        weaponBase: caster.stats.weaponBase * scale,
        atkBonus: caster.stats.atkBonus,
        elementBonus: { ...caster.stats.elementBonus },
        critRate: caster.stats.critRate,
        critDamage: caster.stats.critDamage,
        armor: caster.stats.armor * scale,
        resistance: { ...caster.stats.resistance },
        accuracy: caster.stats.accuracy,
        evasion: caster.stats.evasion,
        attackInterval: caster.stats.attackInterval,
        moveSpeed: caster.stats.moveSpeed * scale,
    };
    // Spread summons in a tight ring around the caster so they don't all stack
    // at the exact same point (prevents AI / movement degenerate cases).
    const angle = (index / Math.max(1, spec.count ?? 1)) * 2 * Math.PI;
    const offset = 0.5;
    const pos = {
        x: caster.pos.x + Math.cos(angle) * offset,
        y: caster.pos.y + Math.sin(angle) * offset,
    };
    const entity = {
        id: nextRuntimeId(state),
        side: caster.side,
        level: caster.level,
        element: caster.element,
        weaponElement: caster.weaponElement ?? 'physical',
        pos,
        radius: caster.radius,
        velocity: { x: 0, y: 0 },
        hp: stats.hpMax,
        hpMax: stats.hpMax,
        shields: [],
        stats,
        jobTypes: caster.jobTypes,
        cooldowns: new Map(),
        buffs: [],
        debuffs: [],
        ultimate: null,
        itemSkills: [],
        behaviorRules: caster.behaviorRules,
        speedMods: { actionSpeed: 0, attackSpeed: 0, cooldownRate: 0, moveSpeed: 0 },
        damageReduction: 0,
        summonPowerBonus: 0,
        placeablePowerBonus: 0,
        threatMod: caster.threatMod,
        isPet: true,
    };
    return entity;
}
/**
 * Public helper used by `effects.ts#summon` arm: spawn the summons and (if
 * threadLineAura is present in the spec) attach a snapshot to each instance
 * so `tickSummons` can refresh the aura without re-reading the spec.
 */
export function spawnSummonsAttachingAura(spec, caster, state) {
    const created = spawnSummons(spec, caster, state);
    if (spec.threadLineAura !== undefined) {
        const snapshot = {
            buffId: spec.threadLineAura.buffId,
            auraRadius: spec.threadLineAura.auraRadius,
        };
        if (spec.threadLineAura.buffDurationTicks !== undefined) {
            snapshot.buffDurationTicks = spec.threadLineAura.buffDurationTicks;
        }
        for (const inst of created) {
            Object.defineProperty(inst, '_threadLineAura', {
                value: snapshot,
                enumerable: false,
                writable: false,
                configurable: false,
            });
        }
    }
    return created;
}
//# sourceMappingURL=summons.js.map